import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Edit,
  Trash2,
  Play,
  Pause,
  Square,
  CalendarDays,
  Plus,
  Copy,
  Download,
  Filter,
  Receipt,
  ReceiptText,
  PlayCircle,
  Search,
  X,
} from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { DataTable } from "@/components/DataTable";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useProjects } from "@/hooks/useProjects";
import {
  useTimeEntries,
  useActiveTimer,
  useCreateTimeEntry,
  useDeleteTimeEntry,
  useUpdateTimeEntry,
  useStopTimer,
} from "@/hooks/useTimeEntries";
import {
  useTimeEntriesFiltered,
  type TimeEntryFilters,
} from "@/hooks/useTimeEntriesFiltered";
import { useDailySummary } from "@/hooks/useTimerSummary";
import { useAuth } from "@/hooks/useAuth";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { useTimer } from "@/hooks/useTimer";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { EditTimeEntryDialog } from "@/components/timer/EditTimeEntryDialog";
import { ManualEntryDialog } from "@/components/timer/ManualEntryDialog";
import {
  TimeGoalInput,
  type GoalBehavior,
} from "@/components/timer/TimeGoalInput";
import { DailySummaryCards } from "@/components/timer/DailySummaryCards";
import {
  CategorySelect,
  defaultCategories,
  getCategoryDisplay,
} from "@/components/timer/CategorySelect";
import {
  TimerFiltersDialog,
  type TimerFilters,
  getActiveFiltersCount,
  hasCustomDateRange,
} from "@/components/timer/TimerFiltersDialog";
import { usePauseTimer } from "@/hooks/usePauseTimer";
import type { Database } from "@/integrations/supabase/types";

// Helper functions moved outside component to prevent re-creation
const formatTime = (totalSeconds: number) => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;
  return `${hours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
};


const formatDuration = (minutes: number | null) => {
  if (!minutes) return "-";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}min`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}min`;
};

const formatTimeStr = (dateStr: string) => {
  return format(new Date(dateStr), "HH:mm");
};

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
};

type TimeCategory = Database["public"]["Enums"]["time_category"];

interface TimerState {
  selectedProject: string;
  selectedCategory: TimeCategory;
  customCategory: string;
  description: string;
  hourlyRate: string;
  goalMinutes: number | null;
  goalBehavior: GoalBehavior;
  goalReached: boolean;
  isBillable: boolean;
}

const defaultTimerState: TimerState = {
  selectedProject: "",
  selectedCategory: "dev",
  customCategory: "",
  description: "",
  hourlyRate: "",
  goalMinutes: null,
  goalBehavior: "alert_only",
  goalReached: false,
  isBillable: true,
};

export default function TimerPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const { data: projects } = useProjects(); // Use new centralized timer hook
  const { 
    activeTimer, 
    seconds, 
    isPaused, 
    start: startTimer, 
    stop: stopTimerRef, 
    pause: pauseTimer, 
    resume: resumeTimer 
  } = useTimer();

  const createTimeEntry = useCreateTimeEntry();
  const deleteTimeEntry = useDeleteTimeEntry();
  const updateTimeEntry = useUpdateTimeEntry(); // Keep for editing

  // Timer UI state

  const [date, setDate] = useState<Date>(new Date());
  const dateStr = format(date, "yyyy-MM-dd");
  const { data: dailySummary, isLoading: summaryLoading } =
    useDailySummary(dateStr);

  // Advanced filters
  const [filtersDialogOpen, setFiltersDialogOpen] = useState(false);
  
  // Memoize initial filters to prevent infinite loop in useLocalStorage
  // (useLocalStorage updates state when initialValue changes reference)
  const initialFilters = useMemo(() => ({
      startDate: new Date(),
      endDate: new Date(),
      projectId: "",
      category: "",
      searchText: "",
      billableOnly: null,
  }), []);

  const [rawAdvancedFilters, setAdvancedFilters] = useLocalStorage<TimerFilters>(
    "timer-advanced-filters",
    initialFilters,
  );

  // Ensure dates are always Date objects (localStorage serializes them as strings)
  // useMemo prevents creating a new object on every render (timer re-renders every 1s)
  const advancedFilters: TimerFilters = useMemo(() => ({
    ...rawAdvancedFilters,
    startDate: rawAdvancedFilters.startDate ? new Date(rawAdvancedFilters.startDate) : null,
    endDate: rawAdvancedFilters.endDate ? new Date(rawAdvancedFilters.endDate) : null,
  }), [rawAdvancedFilters]);

  // Use filtered query
  const { data: timeEntries, isLoading: entriesLoading } =
    useTimeEntriesFiltered({
      startDate: advancedFilters.startDate,
      endDate: advancedFilters.endDate,
      projectId: advancedFilters.projectId,
      category: advancedFilters.category,
      searchText: advancedFilters.searchText,
      billableOnly: advancedFilters.billableOnly,
    });

  const [timerState, setTimerState] = useLocalStorage<TimerState>(
    "timer-state",
    defaultTimerState,
  );

  // Dialogs
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [manualDialogOpen, setManualDialogOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<any>(null);

  // Audio ref for goal alert
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Derived state for UI
  const isRunning = !!activeTimer && !isPaused;

  const handleStop = async () => {
    if (!activeTimer) return;

    try {
      await stopTimerRef({
        project_id: timerState.selectedProject || null,
        category: timerState.selectedCategory,
        custom_category: (timerState.customCategory || "").trim() || null,
        description: (timerState.description || "").trim() || null,
        is_billable: timerState.isBillable,
        hourly_rate: timerState.hourlyRate
          ? parseFloat(timerState.hourlyRate)
          : null,
      });

      setTimerState((prev) => ({
        ...prev,
        description: "",
        selectedProject: "",
        customCategory: "",
        hourlyRate: "",
        goalReached: false,
      }));
      toast({
        title: "Timer parado!",
        description: "Tempo registrado com sucesso.",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível parar o timer.",
      });
    }
  };

  // Check if goal is reached
  useEffect(() => {
    if (!isRunning || !timerState.goalMinutes || timerState.goalReached) return;

    const currentMinutes = Math.floor(seconds / 60);
    if (currentMinutes >= timerState.goalMinutes) {
      // Goal reached!
      setTimerState((prev) => ({ ...prev, goalReached: true }));

      // Play sound alert
      const playNotificationSound = () => {
        try {
          // Try to play file first
          const audio = new Audio("/notification.mp3");
          audio.volume = 0.5;
          const promise = audio.play();
          
          if (promise !== undefined) {
            promise.catch(() => {
              // Fallback to Web Audio API beep if file fails or policy blocks
              try {
                const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
                if (!AudioContext) return;
                
                const ctx = new AudioContext();
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                
                osc.connect(gain);
                gain.connect(ctx.destination);
                
                osc.type = "sine";
                osc.frequency.setValueAtTime(880, ctx.currentTime); // A5
                osc.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.5); // Drop to A4
                
                gain.gain.setValueAtTime(0.3, ctx.currentTime);
                gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
                
                osc.start();
                osc.stop(ctx.currentTime + 0.5);
              } catch (e) {
                console.error("Audio fallback failed", e);
              }
            });
          }
        } catch (e) {
          console.error("Audio play failed", e);
        }
      };
      
      playNotificationSound();

      toast({
        title: "🎯 Meta atingida!",
        description: `Você completou ${timerState.goalMinutes} minutos de trabalho.`,
      });

      // Auto stop if configured
      if (timerState.goalBehavior === "auto_stop" && activeTimer) {
        handleStop();
      }
    }
  }, [
    seconds,
    timerState.goalMinutes,
    timerState.goalReached,
    timerState.goalBehavior,
    isRunning,
    activeTimer,
  ]);

  // Sync hourly rate when project is selected (for hourly projects)
  useEffect(() => {
    if (!timerState.selectedProject || !projects) return;

    const selectedProject = projects.find(
      (p) => p.id === timerState.selectedProject,
    );
    if (
      selectedProject &&
      selectedProject.billing_type === "hourly" &&
      selectedProject.hourly_rate
    ) {
      setTimerState((prev) => ({
        ...prev,
        hourlyRate: selectedProject.hourly_rate!.toString(),
      }));
    }
  }, [timerState.selectedProject, projects]);

  // Set hourly rate to 0 when non-billable
  useEffect(() => {
    if (!timerState.isBillable) {
      setTimerState((prev) => ({
        ...prev,
        hourlyRate: "0",
      }));
    } else if (timerState.selectedProject && projects) {
      // Restore project rate when billable is enabled again
      const selectedProject = projects.find(
        (p) => p.id === timerState.selectedProject,
      );
      if (
        selectedProject &&
        selectedProject.billing_type === "hourly" &&
        selectedProject.hourly_rate
      ) {
        setTimerState((prev) => ({
          ...prev,
          hourlyRate: selectedProject.hourly_rate!.toString(),
        }));
      }
    }
  }, [timerState.isBillable, timerState.selectedProject, projects]);

  // Refs para evitar stale closures no keyboard shortcut
  const handleStartRef = useRef(() => {});
  const handleStopRef = useRef(() => {});
  const handlePauseRef = useRef(() => {});
  const handleResumeRef = useRef(() => {});


  // Keyboard shortcuts: Space = start/pause/resume, Escape = stop
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      // Não dispara em inputs, textareas, selects ou dialogs
      const isInteractive =
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.tagName === "SELECT" ||
        target.isContentEditable ||
        !!target.closest('[role="dialog"]');

      if (isInteractive) return;

      if (e.code === "Space") {
        e.preventDefault();
        if (!activeTimer) {
          handleStartRef.current();
        } else if (isPaused) {
          handleResumeRef.current();
        } else {
          handlePauseRef.current();
        }
      }

      // Escape para parar o timer
      if (e.code === "Escape" && activeTimer) {
        e.preventDefault();
        handleStopRef.current();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeTimer, isPaused]);


  const handleStart = async () => {
    if (!user?.id) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Você precisa estar logado para iniciar o timer.",
      });
      return;
    }

    await startTimer({
      project_id: timerState.selectedProject || null,
      category: timerState.selectedCategory,
      custom_category: (timerState.customCategory || "").trim() || null,
      description: (timerState.description || "").trim() || null,
      is_billable: timerState.isBillable,
      hourly_rate: timerState.hourlyRate
        ? parseFloat(timerState.hourlyRate)
        : null,
    });

    setTimerState((prev) => ({ ...prev, goalReached: false }));
    toast({
      title: "Timer iniciado!",
      description: "Seu tempo está sendo registrado.",
    });
  };

  const handlePause = async () => {
    if (!activeTimer) return;
    try {
      await pauseTimer();
      toast({ title: "Timer pausado" });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível pausar o timer.",
      });
    }
  };

  const handleResume = async () => {
    if (!activeTimer) return;
    try {
      await resumeTimer();
      toast({ title: "Timer retomado" });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível retomar o timer.",
      });
    }
  };


  // Update refs when dependencies change to ensure they have the latest closure
  // Moved here to be after function definitions to avoid hoisting issues
  useEffect(() => {
    handleStartRef.current = handleStart;
    handleStopRef.current = handleStop;
    handlePauseRef.current = handlePause;
    handleResumeRef.current = handleResume;
  }, [handleStart, handleStop, handlePause, handleResume]);

  const handleDelete = async (id: string) => {
    try {
      await deleteTimeEntry.mutateAsync(id);
      toast({
        title: "Entrada removida",
        description: "A entrada de tempo foi removida.",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível remover a entrada.",
      });
    }
  };

  const handleEdit = (entry: any) => {
    setSelectedEntry(entry);
    setEditDialogOpen(true);
  };

  const handleDuplicate = async (entry: any) => {
    if (!user?.id) return;

    try {
      // Copia TODOS os campos relevantes da entrada original
      await createTimeEntry.mutateAsync({
        user_id: user.id,
        project_id: entry.project_id,
        category: entry.category,
        custom_category: entry.custom_category || null,
        description: entry.description,
        is_billable: entry.is_billable ?? true,
        hourly_rate: entry.hourly_rate || null,
        start_time: new Date().toISOString(),
        end_time: null,
      });

      // Atualiza estado local para refletir a entrada duplicada
      setTimerState((prev) => ({
        ...prev,
        selectedProject: entry.project_id || "",
        selectedCategory: entry.category || "dev",
        customCategory: entry.custom_category || "",
        description: entry.description || "",
        isBillable: entry.is_billable ?? true,
        hourlyRate: entry.hourly_rate?.toString() || "",
        goalReached: false,
      }));

      toast({
        title: "Timer iniciado!",
        description: "Duplicado com as mesmas configurações.",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível duplicar a entrada.",
      });
    }
  };

  const exportToCSV = () => {
    if (!timeEntries || timeEntries.length === 0) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Nenhuma entrada para exportar.",
      });
      return;
    }

    const headers = [
      "Data",
      "Início",
      "Fim",
      "Duração (min)",
      "Projeto",
      "Categoria",
      "Descrição",
      "Faturável",
      "Taxa/Hora",
      "Valor",
    ];
    const rows = timeEntries.map((entry) => {
      const project = entry.projects as { id: string; name: string } | null;
      const duration = entry.duration_minutes || 0;
      const rate = entry.hourly_rate || 0;
      const value = (duration / 60) * rate;

      return [
        format(new Date(entry.start_time), "dd/MM/yyyy"),
        format(new Date(entry.start_time), "HH:mm"),
        entry.end_time ? format(new Date(entry.end_time), "HH:mm") : "",
        duration.toString(),
        project?.name || "",
        entry.category || "",
        entry.description || "",
        entry.is_billable ? "Sim" : "Não",
        rate.toFixed(2),
        value.toFixed(2),
      ];
    });

    const csvContent = [
      headers.join(";"),
      ...rows.map((row) => row.join(";")),
    ].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    const csvDate =
      advancedFilters.startDate && advancedFilters.endDate
        ? `${format(advancedFilters.startDate, "yyyy-MM-dd")}_${format(advancedFilters.endDate, "yyyy-MM-dd")}`
        : format(new Date(), "yyyy-MM-dd");
    link.download = `timesheet-${csvDate}.csv`;
    link.click();

    toast({
      title: "Exportado!",
      description: "O arquivo CSV foi baixado.",
    });
  };



  // Entries already filtered from hook, just use directly
  const filteredEntries = timeEntries || [];

  // Calculate totals
  const totalMinutes = filteredEntries.reduce(
    (acc, entry) => acc + (entry.duration_minutes || 0),
    0,
  );
  const totalValue = filteredEntries.reduce((acc, entry) => {
    const hours = (entry.duration_minutes || 0) / 60;
    const rate = entry.hourly_rate || 0;
    return acc + hours * rate;
  }, 0);

  const tableData = useMemo(
    () =>
      filteredEntries.map((entry) => {
        const project = entry.projects as {
          id: string;
          name: string;
          clients: { id: string; name: string } | null;
        } | null;
        const duration = entry.duration_minutes || 0;
        const rate = entry.hourly_rate || 0;
        const value = (duration / 60) * rate;

        return {
          id: entry.id,
          project_id: entry.project_id,
          category: entry.category,
          custom_category: (entry as any).custom_category,
          description: entry.description,
          start_time: entry.start_time,
          end_time: entry.end_time,
          is_billable: entry.is_billable,
          hourly_rate: entry.hourly_rate,
          start: formatTimeStr(entry.start_time),
          end: entry.end_time ? formatTimeStr(entry.end_time) : "Em andamento",
          duration: formatDuration(entry.duration_minutes),
          projectName: project?.name || "-",
          categoryName: getCategoryDisplay(
            entry.category,
            (entry as any).custom_category,
          ).name,
          categoryColor: getCategoryDisplay(
            entry.category,
            (entry as any).custom_category,
          ).color,
          descriptionDisplay: entry.description
            ? entry.description.length > 40
              ? entry.description.slice(0, 40) + "..."
              : entry.description
            : "-",
          fullDescription: entry.description || "-",
          value: value > 0 ? formatCurrency(value) : "-",
        };
      }),
    [filteredEntries],
  );

  const columns = [
    { key: "start", header: "Início" },
    { key: "end", header: "Fim" },
    { key: "duration", header: "Duração" },
    { key: "projectName", header: "Projeto" },
    {
      key: "categoryName",
      header: "Categoria",
      render: (entry: (typeof tableData)[0]) => (
        <Badge variant="secondary" className={entry.categoryColor}>
          {entry.categoryName}
        </Badge>
      ),
    },
    {
      key: "descriptionDisplay",
      header: "Descrição",
      render: (entry: (typeof tableData)[0]) => (
        <span
          className="block max-w-[200px] truncate"
          title={entry.fullDescription}
        >
          {entry.descriptionDisplay}
        </span>
      ),
    },
    { key: "value", header: "Valor" },
    {
      key: "actions",
      header: "Ações",
      render: (entry: (typeof tableData)[0]) => (
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => handleEdit(entry)}
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => handleDuplicate(entry)}
            title="Duplicar e iniciar"
          >
            <Copy className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-destructive"
            onClick={() => handleDelete(entry.id)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  // Calculate goal progress
  const goalProgress = timerState.goalMinutes
    ? Math.min((Math.floor(seconds / 60) / timerState.goalMinutes) * 100, 100)
    : 0;

  return (
    <DashboardLayout title="Timer">
      <div className="space-y-8">
        {/* Timer Section - Premium Hero Card */}
        <Card className="overflow-hidden border-0 shadow-lg bg-gradient-to-b from-card to-card/95">
          {/* Goal progress bar */}
          {timerState.goalMinutes && isRunning && (
            <div className="h-1.5 bg-muted/50">
              <div
                className={`h-full transition-all duration-500 ease-out ${
                  timerState.goalReached ? "bg-success" : "bg-primary"
                }`}
                style={{ width: `${goalProgress}%` }}
              />
            </div>
          )}

          <CardContent className="p-6 sm:p-10">
            <div className="flex flex-col gap-8">
              {/* Timer Display - Visual Dominant Area */}
              <div className="flex flex-col items-center gap-6 py-4">
                {/* Timer with Bebas Neue */}
                <div
                  className={`font-display text-6xl sm:text-7xl md:text-8xl lg:text-9xl tracking-wider transition-all duration-300 select-none ${
                    isRunning
                      ? timerState.goalReached
                        ? "text-success"
                        : "text-primary animate-pulse-gentle"
                      : isPaused
                        ? "text-muted-foreground"
                        : "text-foreground"
                  }`}
                  style={{
                    textShadow:
                      isRunning && !timerState.goalReached
                        ? "0 4px 24px hsl(var(--primary) / 0.15)"
                        : "none",
                    WebkitTextStroke: "0.5px currentColor",
                  }}
                >
                  {formatTime(seconds)}
                </div>

                {/* Status Badge */}
                {timerState.goalReached && (
                  <Badge className="bg-success text-success-foreground px-4 py-1.5 text-sm font-medium animate-bounce shadow-md">
                    🎯 Meta atingida!
                  </Badge>
                )}

                {isPaused && activeTimer && (
                  <Badge
                    variant="secondary"
                    className="px-4 py-1.5 text-sm font-medium"
                  >
                    ⏸ Pausado
                  </Badge>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3">
                  {!activeTimer ? (
                    <Button
                      onClick={handleStart}
                      size="lg"
                      className="gap-2 rounded-2xl px-8 h-12 text-base font-medium shadow-md hover:shadow-lg transition-all duration-200"
                      disabled={createTimeEntry.isPending}
                    >
                      <Play className="h-5 w-5" />
                      Iniciar
                    </Button>
                  ) : isPaused ? (
                    <Button
                      onClick={handleResume}
                      size="lg"
                      className="gap-2 rounded-2xl px-8 h-12 text-base font-medium shadow-md hover:shadow-lg transition-all duration-200"
                    >
                      <PlayCircle className="h-5 w-5" />
                      Retomar
                    </Button>
                  ) : (
                    <Button
                      onClick={handlePause}
                      variant="secondary"
                      size="lg"
                      className="gap-2 rounded-2xl px-8 h-12 text-base font-medium transition-all duration-200"
                    >
                      <Pause className="h-5 w-5" />
                      Pausar
                    </Button>
                  )}
                  <Button
                    onClick={handleStop}
                    variant="outline"
                    size="lg"
                    className="rounded-2xl h-12 w-12 p-0 transition-all duration-200"
                    disabled={!activeTimer || updateTimeEntry.isPending}
                  >
                    <Square className="h-5 w-5" />
                  </Button>
                </div>

                <p className="text-xs text-muted-foreground hidden sm:block">
                  <kbd className="px-1.5 py-0.5 bg-muted/80 rounded-md text-xs font-medium">
                    Espaço
                  </kbd>{" "}
                  iniciar/pausar{" · "}
                  <kbd className="px-1.5 py-0.5 bg-muted/80 rounded-md text-xs font-medium">
                    Esc
                  </kbd>{" "}
                  parar
                </p>
              </div>

              {/* Form Fields - Clean Inputs Section */}
              <div className="grid w-full gap-4 sm:gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 pt-6 border-t border-border/50">
                <div className="space-y-2.5">
                  <Label className="text-sm font-medium text-foreground">
                    Projeto
                  </Label>
                  <Select
                    value={timerState.selectedProject}
                    onValueChange={(v) =>
                      setTimerState((prev) => ({ ...prev, selectedProject: v }))
                    }
                  >
                    <SelectTrigger className="w-full h-11 rounded-xl border-border/60 bg-background/50 focus:bg-background transition-colors">
                      <SelectValue placeholder="Selecione um projeto" />
                    </SelectTrigger>
                    <SelectContent>
                      {projects?.map((project) => (
                        <SelectItem key={project.id} value={project.id}>
                          {project.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2.5">
                  <Label className="text-sm font-medium text-foreground">
                    Categoria
                  </Label>
                  <CategorySelect
                    value={timerState.selectedCategory}
                    customCategory={timerState.customCategory}
                    onCategoryChange={(v) =>
                      setTimerState((prev) => ({
                        ...prev,
                        selectedCategory: v,
                      }))
                    }
                    onCustomCategoryChange={(v) =>
                      setTimerState((prev) => ({ ...prev, customCategory: v }))
                    }
                  />
                </div>

                <div className="space-y-2.5">
                  <Label className="text-sm font-medium text-foreground">
                    Descrição
                  </Label>
                  <Input
                    value={timerState.description}
                    onChange={(e) =>
                      setTimerState((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    placeholder="O que você está fazendo?"
                    className="h-11 rounded-xl border-border/60 bg-background/50 focus:bg-background transition-colors"
                  />
                </div>

                <div className="space-y-2.5">
                  <Label className="text-sm font-medium text-foreground">
                    Valor/Hora
                  </Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                      R$
                    </span>
                    <Input
                      type="number"
                      value={timerState.hourlyRate}
                      onChange={(e) =>
                        setTimerState((prev) => ({
                          ...prev,
                          hourlyRate: e.target.value,
                        }))
                      }
                      placeholder="0,00"
                      className="h-11 pl-10 rounded-xl border-border/60 bg-background/50 focus:bg-background transition-colors"
                    />
                  </div>
                </div>

                <TimeGoalInput
                  goalMinutes={timerState.goalMinutes}
                  onGoalChange={(mins) =>
                    setTimerState((prev) => ({ ...prev, goalMinutes: mins }))
                  }
                  goalBehavior={timerState.goalBehavior}
                  onBehaviorChange={(behavior) =>
                    setTimerState((prev) => ({
                      ...prev,
                      goalBehavior: behavior,
                    }))
                  }
                  disabled={isRunning}
                />
              </div>

              {/* Billable Toggle - Subtle Footer */}
              <div className="flex items-center justify-center gap-3 pt-4">
                <div className="flex items-center gap-3 px-4 py-2 rounded-xl bg-muted/30">
                  <Switch
                    id="billable-toggle"
                    checked={timerState.isBillable}
                    onCheckedChange={(checked) =>
                      setTimerState((prev) => ({
                        ...prev,
                        isBillable: checked,
                      }))
                    }
                  />
                  <Label
                    htmlFor="billable-toggle"
                    className="cursor-pointer flex items-center gap-2 text-sm"
                  >
                    {timerState.isBillable ? (
                      <>
                        <Receipt className="h-4 w-4 text-success" />
                        <span className="text-success font-medium">
                          Faturável
                        </span>
                      </>
                    ) : (
                      <>
                        <ReceiptText className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">
                          Não faturável
                        </span>
                      </>
                    )}
                  </Label>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Daily Summary */}
        <DailySummaryCards
          totalMinutes={dailySummary?.totalMinutes || 0}
          billableMinutes={dailySummary?.billableMinutes || 0}
          nonBillableMinutes={dailySummary?.nonBillableMinutes || 0}
          billableValue={dailySummary?.billableValue || 0}
          isLoading={summaryLoading}
        />

        {/* Time Entries */}
        <div className="space-y-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-xl font-semibold tracking-tight">Entradas</h2>
              {/* Active date range indicator */}
              {advancedFilters.startDate && advancedFilters.endDate && (
                <Badge
                  variant="outline"
                  className="gap-1.5 font-normal text-muted-foreground"
                >
                  <CalendarDays className="h-3 w-3" />
                  {format(advancedFilters.startDate, "dd/MM", { locale: ptBR })}
                  {format(advancedFilters.startDate, "yyyy-MM-dd") !==
                    format(advancedFilters.endDate, "yyyy-MM-dd") && (
                    <>
                      {" "}
                      —{" "}
                      {format(advancedFilters.endDate, "dd/MM", {
                        locale: ptBR,
                      })}
                    </>
                  )}
                </Badge>
              )}
              {/* Active filter chips */}
              {advancedFilters.projectId && advancedFilters.projectId !== "__all__" && (
                <Badge variant="secondary" className="gap-1 text-xs font-normal">
                  Projeto
                  <X
                    className="h-3 w-3 cursor-pointer hover:text-destructive transition-colors"
                    onClick={() => setAdvancedFilters({ ...advancedFilters, projectId: "" })}
                  />
                </Badge>
              )}
              {advancedFilters.category && advancedFilters.category !== "__all__" && (
                <Badge variant="secondary" className="gap-1 text-xs font-normal">
                  Categoria
                  <X
                    className="h-3 w-3 cursor-pointer hover:text-destructive transition-colors"
                    onClick={() => setAdvancedFilters({ ...advancedFilters, category: "" })}
                  />
                </Badge>
              )}
              {advancedFilters.searchText?.trim() && (
                <Badge variant="secondary" className="gap-1 text-xs font-normal">
                  &quot;{advancedFilters.searchText}&quot;
                  <X
                    className="h-3 w-3 cursor-pointer hover:text-destructive transition-colors"
                    onClick={() => setAdvancedFilters({ ...advancedFilters, searchText: "" })}
                  />
                </Badge>
              )}
              {advancedFilters.billableOnly !== null && advancedFilters.billableOnly !== undefined && (
                <Badge variant="secondary" className="gap-1 text-xs font-normal">
                  {advancedFilters.billableOnly ? "Faturável" : "Não faturável"}
                  <X
                    className="h-3 w-3 cursor-pointer hover:text-destructive transition-colors"
                    onClick={() => setAdvancedFilters({ ...advancedFilters, billableOnly: null })}
                  />
                </Badge>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {/* Advanced Filters Button */}
              <Button
                variant={getActiveFiltersCount(advancedFilters) > 0 ? "default" : "outline"}
                size="sm"
                className="gap-2 rounded-xl border-border/60 hover:bg-muted/50 transition-colors"
                onClick={() => setFiltersDialogOpen(true)}
              >
                <Filter className="h-4 w-4" />
                Filtrar
                {getActiveFiltersCount(advancedFilters) > 0 && (
                  <Badge
                    variant="secondary"
                    className="ml-1 h-5 w-5 p-0 flex items-center justify-center rounded-full text-xs bg-primary-foreground/20"
                  >
                    {getActiveFiltersCount(advancedFilters)}
                  </Badge>
                )}
              </Button>

              <Button
                variant="outline"
                size="sm"
                className="gap-2 rounded-xl border-border/60 hover:bg-muted/50 transition-colors"
                onClick={() => setManualDialogOpen(true)}
              >
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline">Entrada Manual</span>
                <span className="sm:hidden">Manual</span>
              </Button>

              <Button
                variant="outline"
                size="sm"
                className="gap-2 rounded-xl border-border/60 hover:bg-muted/50 transition-colors"
                onClick={exportToCSV}
              >
                <Download className="h-4 w-4" />
                <span className="hidden sm:inline">Exportar CSV</span>
              </Button>
            </div>
          </div>

          <Card className="border-0 shadow-md overflow-hidden">
            <CardContent className="p-0">
              {entriesLoading ? (
                <div className="p-6 space-y-4">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-12" />
                  ))}
                </div>
              ) : tableData.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  Nenhuma entrada encontrada para os filtros selecionados.
                </p>
              ) : (
                <>
                  {/* Mobile: Card view */}
                  <div className="block sm:hidden divide-y">
                    {tableData.map((entry) => (
                      <div key={entry.id} className="p-4 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-sm">
                            {entry.projectName}
                          </span>
                          <Badge
                            variant="secondary"
                            className={`${entry.categoryColor} text-xs`}
                          >
                            {entry.categoryName}
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between text-sm text-muted-foreground">
                          <span>
                            {entry.start} - {entry.end}
                          </span>
                          <span className="font-medium text-foreground">
                            {entry.duration}
                          </span>
                        </div>
                        {entry.fullDescription !== "-" && (
                          <p
                            className="text-sm text-muted-foreground truncate max-w-full"
                            title={entry.fullDescription}
                          >
                            {entry.descriptionDisplay}
                          </p>
                        )}
                        <div className="flex items-center justify-between pt-2">
                          <span className="text-sm font-medium text-primary">
                            {entry.value}
                          </span>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleEdit(entry)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleDuplicate(entry)}
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive"
                              onClick={() => handleDelete(entry.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Desktop: Table view */}
                  <div className="hidden sm:block">
                    <DataTable data={tableData} columns={columns} />
                  </div>

                  {/* Totals Footer */}
                  <div className="border-t border-border/50 p-4 sm:p-5 flex flex-col sm:flex-row gap-3 sm:justify-between sm:items-center bg-muted/20">
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-muted-foreground">
                        Total:{" "}
                        <strong className="text-foreground font-semibold">
                          {formatDuration(totalMinutes)}
                        </strong>
                      </span>
                      <span className="text-muted-foreground text-xs">
                        ({filteredEntries.length}{" "}
                        {filteredEntries.length === 1 ? "entrada" : "entradas"})
                      </span>
                    </div>
                    {totalValue > 0 && (
                      <span className="text-sm font-semibold text-primary">
                        Faturado: {formatCurrency(totalValue)}
                      </span>
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Dialogs */}
        <EditTimeEntryDialog
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          entry={selectedEntry}
        />
        <ManualEntryDialog
          open={manualDialogOpen}
          onOpenChange={setManualDialogOpen}
        />
        <TimerFiltersDialog
          open={filtersDialogOpen}
          onOpenChange={setFiltersDialogOpen}
          filters={advancedFilters}
          onFiltersChange={setAdvancedFilters}
          projects={projects || []}
        />
      </div>
    </DashboardLayout>
  );
}
